import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Fade,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import axios from "axios";
import { baseUrl } from "../../constants";

// Icons
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Types
interface Branch {
  id: number;
  workspaceId: number;
  code: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  userCount: number;
}

interface BranchUser {
  id: number;
  username: string;
  profile?: {
    name?: string;
    email?: string;
    imageUrl?: string;
  };
}

// Removed unused interface

const Branches: React.FC = () => {
  const theme = useTheme();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState<boolean | null>(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [branchUsers, setBranchUsers] = useState<BranchUser[]>([]);

  // Form states
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    isActive: true,
  });

  // Menu states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuBranch, setMenuBranch] = useState<Branch | null>(null);

  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });

  // Fetch branches
  const fetchBranches = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
      });

      if (searchTerm) params.append("search", searchTerm);
      if (activeFilter !== null) params.append("isActive", activeFilter.toString());

      const response = await axios.get(`${baseUrl}/branches?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setBranches(response.data.data);
      setTotalCount(response.data.pagination.total);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch branches",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, activeFilter]);

  // Fetch branch users
  const fetchBranchUsers = async (branchId: number) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(`${baseUrl}/branches/${branchId}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setBranchUsers(response.data.users);
      setUsersDialogOpen(true);
    } catch (error) {
      console.error("Error fetching branch users:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch branch users",
        severity: "error",
      });
    }
  };

  // Create branch
  const handleCreateBranch = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(
        `${baseUrl}/branches`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSnackbar({
        open: true,
        message: "Branch created successfully",
        severity: "success",
      });
      setCreateDialogOpen(false);
      setFormData({ code: "", name: "", isActive: true });
      fetchBranches();
    } catch (error: unknown) {
      console.error("Error creating branch:", error);
      const errorMessage = error instanceof Error && 'response' in error 
        ? (error as any).response?.data?.message || "Failed to create branch"
        : "Failed to create branch";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  // Update branch
  const handleUpdateBranch = async () => {
    if (!selectedBranch) return;

    try {
      const token = localStorage.getItem("accessToken");
      await axios.patch(
        `${baseUrl}/branches/${selectedBranch.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSnackbar({
        open: true,
        message: "Branch updated successfully",
        severity: "success",
      });
      setEditDialogOpen(false);
      setSelectedBranch(null);
      setFormData({ code: "", name: "", isActive: true });
      fetchBranches();
    } catch (error: unknown) {
      console.error("Error updating branch:", error);
      const errorMessage = error instanceof Error && 'response' in error 
        ? (error as any).response?.data?.message || "Failed to update branch"
        : "Failed to update branch";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  // Delete branch
  const handleDeleteBranch = async (branch: Branch) => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.delete(`${baseUrl}/branches/${branch.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSnackbar({
        open: true,
        message: "Branch deleted successfully",
        severity: "success",
      });
      fetchBranches();
    } catch (error: unknown) {
      console.error("Error deleting branch:", error);
      const errorMessage = error instanceof Error && 'response' in error 
        ? (error as any).response?.data?.message || "Failed to delete branch"
        : "Failed to delete branch";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  // Toggle active status
  const handleToggleActive = async (branch: Branch) => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.patch(
        `${baseUrl}/branches/${branch.id}/toggle-active`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSnackbar({
        open: true,
        message: `Branch ${branch.isActive ? "deactivated" : "activated"} successfully`,
        severity: "success",
      });
      fetchBranches();
    } catch (error: unknown) {
      console.error("Error toggling branch status:", error);
      const errorMessage = error instanceof Error && 'response' in error 
        ? (error as any).response?.data?.message || "Failed to update branch status"
        : "Failed to update branch status";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  // Handle menu actions
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, branch: Branch) => {
    setAnchorEl(event.currentTarget);
    setMenuBranch(branch);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuBranch(null);
  };

  const handleEditClick = () => {
    if (menuBranch) {
      setSelectedBranch(menuBranch);
      setFormData({
        code: menuBranch.code,
        name: menuBranch.name,
        isActive: menuBranch.isActive,
      });
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (menuBranch) {
      handleDeleteBranch(menuBranch);
    }
    handleMenuClose();
  };

  const handleViewUsersClick = () => {
    if (menuBranch) {
      fetchBranchUsers(menuBranch.id);
    }
    handleMenuClose();
  };

  // Effects
  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  // Handlers
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleFilterChange = (filter: boolean | null) => {
    setActiveFilter(filter);
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Branch Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage workspace branches and their users
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            px: 3,
            py: 1.5,
          }}
        >
          Add Branch
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            placeholder="Search branches..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
            size="small"
          />
          
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label="All"
              onClick={() => handleFilterChange(null)}
              color={activeFilter === null ? "primary" : "default"}
              variant={activeFilter === null ? "filled" : "outlined"}
            />
            <Chip
              label="Active"
              onClick={() => handleFilterChange(true)}
              color={activeFilter === true ? "primary" : "default"}
              variant={activeFilter === true ? "filled" : "outlined"}
            />
            <Chip
              label="Inactive"
              onClick={() => handleFilterChange(false)}
              color={activeFilter === false ? "primary" : "default"}
              variant={activeFilter === false ? "filled" : "outlined"}
            />
          </Box>

          <IconButton onClick={fetchBranches} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Branches Table */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : branches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No branches found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                branches.map((branch) => (
                  <Fade in key={branch.id} timeout={300}>
                    <TableRow hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {branch.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {branch.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={branch.isActive ? "Active" : "Inactive"}
                          color={branch.isActive ? "success" : "default"}
                          size="small"
                          icon={branch.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PeopleIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {branch.userCount} users
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(branch.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                          <Tooltip title="View Users">
                            <IconButton
                              size="small"
                              onClick={() => fetchBranchUsers(branch.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Toggle Status">
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActive(branch)}
                            >
                              {branch.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, branch)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </Fade>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Create Branch Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Branch</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Branch Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
              helperText="Unique identifier for the branch"
            />
            <TextField
              label="Branch Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateBranch} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Branch Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Branch</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Branch Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Branch Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateBranch} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Branch Users Dialog */}
      <Dialog open={usersDialogOpen} onClose={() => setUsersDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PeopleIcon />
            <Typography variant="h6">
              {selectedBranch ? `${selectedBranch.name} Users` : "Branch Users"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {branchUsers.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No users assigned to this branch
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {branchUsers.map((user) => (
                <Grid item xs={12} sm={6} md={4} key={user.id}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                          src={user.profile?.imageUrl}
                          sx={{ width: 40, height: 40 }}
                        >
                          {user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {user.profile?.name || user.username}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            @{user.username}
                          </Typography>
                          {user.profile?.email && (
                            <Typography variant="caption" color="text.secondary">
                              {user.profile.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewUsersClick}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Users</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Branches;
